import { useEffect, useState } from "react";
import { RotateCw, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { registerServiceWorker } from "@/lib/pwa/registerServiceWorker";

/**
 * One-tap "Update available" snackbar.
 *
 * Registers the service worker (guarded — does nothing in the Lovable
 * editor preview iframe or on preview hosts) and surfaces a snackbar when
 * a new bundle is waiting. Clicking Reload tells the SW to `skipWaiting`
 * and reloads as soon as it takes control.
 */
export function UpdatePrompt() {
  const [reload, setReload] = useState<(() => void) | null>(null);

  useEffect(() => {
    let cancelled = false;
    registerServiceWorker((doReload) => {
      if (!cancelled) setReload(() => doReload);
    }).catch(() => {
      /* registerServiceWorker already logs failures */
    });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!reload) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed inset-x-2 z-[60] flex items-center gap-3 rounded-xl border border-border bg-card/95 p-3 shadow-lg backdrop-blur-sm md:inset-x-auto md:left-1/2 md:max-w-sm md:-translate-x-1/2"
      style={{
        bottom: `calc(env(safe-area-inset-bottom) + 5.5rem)`,
      }}
    >
      <RotateCw className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
      <p className="flex-1 text-sm text-foreground">
        A new version of Smplify is ready.
      </p>
      <Button size="sm" onClick={() => reload()} className="shrink-0">
        Reload
      </Button>
      <Button
        variant="ghost"
        size="icon"
        onClick={() => setReload(null)}
        aria-label="Dismiss update notice"
        className="h-8 w-8 shrink-0"
      >
        <X className="h-4 w-4" />
      </Button>
    </div>
  );
}

export default UpdatePrompt;
